import React, { createContext, useContext, useState, ReactNode } from 'react';
import type { ColorScheme } from '@@/types/Global';

interface GlobalState {
  isContactModalOpen: boolean;
  userEmail: string;
  userName: string;
  companyId: string;
  isSideNavExpanded: boolean;
  colorScheme: ColorScheme;
  supportTicketsRefreshKey: number;
  isCasesModalOpen: boolean;
  casesRefreshKey: number;
}

interface CasesActions {
  openModal: () => void;
  closeModal: () => void;
  toggleModal: () => void;
  refresh: () => void;
}

interface GlobalActions {
  openContactModal: () => void;
  closeContactModal: () => void;
  toggleContactModal: () => void;
  setUserEmail: (email: string) => void;
  setUserName: (name: string) => void;
  setCompanyId: (id: string) => void;
  setSideNavExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  toggleSideNav: () => void;
  setColorScheme: (scheme: ColorScheme) => void;
  refreshSupportTickets: () => void;
  cases: CasesActions;
}

interface GlobalContextType extends GlobalState, GlobalActions {}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

interface GlobalProviderProps {
  children: ReactNode;
}

export const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [isSideNavExpanded, setIsSideNavExpanded] = useState(false);
  const [supportTicketsRefreshKey, setSupportTicketsRefreshKey] = useState(0);
  const [colorScheme, setColorScheme] = useState<ColorScheme>(() => {
    if (typeof window === 'undefined') {
      return 'cxportal';
    }
    const stored = window.localStorage.getItem('colorScheme');
    return stored === 'cxportal' || stored === 'cxcentral' || stored === 'cases' ? stored : 'cxportal';
  });
  const [isCasesModalOpen, setIsCasesModalOpen] = useState(false);
  const [casesRefreshKey, setCasesRefreshKey] = useState(0);
  const openCasesModal = () => setIsCasesModalOpen(true);
  const closeCasesModal = () => setIsCasesModalOpen(false);
  const toggleCasesModal = () => setIsCasesModalOpen((prev) => !prev);
  const openContactModal = () => setIsContactModalOpen(true);
  const closeContactModal = () => setIsContactModalOpen(false);
  const toggleContactModal = () => setIsContactModalOpen((prev) => !prev);

  const toggleSideNav = () => setIsSideNavExpanded((prev) => !prev);
  const updateColorScheme = (scheme: ColorScheme) => {
    setColorScheme(scheme);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('colorScheme', scheme);
    }
  };
  const refreshSupportTickets = () => setSupportTicketsRefreshKey((k) => k + 1);
  const refreshCases = () => setCasesRefreshKey((k) => k + 1);

  const contextValue: GlobalContextType = {
    isContactModalOpen,
    userEmail,
    userName,
    companyId,
    isSideNavExpanded,
    colorScheme,
    supportTicketsRefreshKey,
    openContactModal,
    closeContactModal,
    toggleContactModal,
    setUserEmail,
    setUserName,
    setCompanyId,
    setSideNavExpanded: setIsSideNavExpanded,
    toggleSideNav,
    setColorScheme: updateColorScheme,
    refreshSupportTickets,
    isCasesModalOpen,
    casesRefreshKey,
    cases: {
      openModal: openCasesModal,
      closeModal: closeCasesModal,
      toggleModal: toggleCasesModal,
      refresh: refreshCases
    }
  };

  return <GlobalContext.Provider value={contextValue}>{children}</GlobalContext.Provider>;
};

export const useGlobal = (): GlobalContextType => {
  const context = useContext(GlobalContext);

  if (context === undefined) {
    throw new Error('useGlobal must be used within a GlobalProvider');
  }

  return context;
};

export default GlobalContext;
